import { useEffect, useRef, useState } from 'react';

const CLICK_DEBOUNCE_MS = 250;
const HINT_AUTO_HIDE_MS = 3400;
const HINT_LAST_SHOWN_KEY = 'scrollerHintShownDate';

const todayIso = () => new Date().toISOString().slice(0, 10);

const readLastHintDate = () => {
	try {
		return localStorage.getItem(HINT_LAST_SHOWN_KEY);
	} catch (e) {
		return null;
	}
};

const writeLastHintDate = (value) => {
	try {
		localStorage.setItem(HINT_LAST_SHOWN_KEY, value);
	} catch (e) {
		return;
	}
};

export const useScroller = ({ step, scrollStep, showControlsAlways }) => {
	const ref = useRef(null);
	const pendingClickRef = useRef(null);
	const hintTimerRef = useRef(null);
	const [scrollState, setScrollState] = useState({ L: false, R: false });
	const [hintActive, setHintActive] = useState(false);

	useEffect(() => {
		const today = todayIso();

		if (readLastHintDate() === today) return;

		writeLastHintDate(today);
		setHintActive(true);

		hintTimerRef.current = setTimeout(() => {
			hintTimerRef.current = null;
			setHintActive(false);
		}, HINT_AUTO_HIDE_MS);

		return () => {
			if (hintTimerRef.current !== null) {
				clearTimeout(hintTimerRef.current);

				hintTimerRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		const el = ref.current;

		if (!el) return;

		const update = () => {
			const max = el.scrollWidth - el.clientWidth;
			const next = { L: el.scrollLeft > 1, R: el.scrollLeft < max - 1 };

			setScrollState((prev) => (prev.L === next.L && prev.R === next.R ? prev : next));
		};

		el.addEventListener('scroll', update, { passive: true });

		const ro = new ResizeObserver(() => requestAnimationFrame(update));

		ro.observe(el);
		requestAnimationFrame(update);

		return () => {
			el.removeEventListener('scroll', update);
			ro.disconnect();
		};
	}, []);

	const cancelPendingClick = () => {
		if (pendingClickRef.current !== null) {
			clearTimeout(pendingClickRef.current);

			pendingClickRef.current = null;
		}
	};

	useEffect(() => () => cancelPendingClick(), []);

	const jump = () => scrollStep ?? (ref.current ? ref.current.clientWidth * step : 0);

	const scrollByX = (dx) => {
		const el = ref.current;

		if (!el) return;

		el.scrollBy({ left: dx, behavior: 'smooth' });
	};

	const scrollToEdge = (edge) => {
		const el = ref.current;

		if (!el) return;

		el.scrollTo({
			left: edge === 'start' ? 0 : el.scrollWidth - el.clientWidth,
			behavior: 'smooth',
		});
	};

	const scheduleStep = (dx) => {
		cancelPendingClick();

		pendingClickRef.current = setTimeout(() => {
			pendingClickRef.current = null;

			scrollByX(dx);
		}, CLICK_DEBOUNCE_MS);
	};

	const handleDoubleClickToEdge = (edge) => {
		cancelPendingClick();
		scrollToEdge(edge);
	};

	const hasOverflow = scrollState.L || scrollState.R;
	const showLeft = showControlsAlways ? hasOverflow : scrollState.L;
	const showRight = showControlsAlways ? hasOverflow : scrollState.R;
	const leftDisabled = !scrollState.L;
	const rightDisabled = !scrollState.R;

	return {
		ref,
		hintActive,
		showLeft,
		showRight,
		leftDisabled,
		rightDisabled,
		onClickLeft: () => scheduleStep(-jump()),
		onClickRight: () => scheduleStep(jump()),
		onDoubleClickLeft: () => handleDoubleClickToEdge('start'),
		onDoubleClickRight: () => handleDoubleClickToEdge('end'),
	};
};
